<template>
  <div class="relative">
    <!-- Product background image with transparent mask -->
    <div
      v-if="checkoutStore.product?.backgroundImage"
      class="absolute inset-0 overflow-hidden brightness-40"
    >
      <strapi-background-image :image="checkoutStore.product.backgroundImage" />
    </div>

    <div class="relative max-w-screen-xl mx-auto px-4 pt-8 pb-4 md:pb-8">
      <common-breadcrumb class="mb-4" :crumbs="breadcrumbs" />
      <!-- Product header -->
      <div v-if="checkoutStore.product" class="mb-6 flex items-center gap-4">
        <div class="relative shrink-0">
          <img
            v-if="checkoutStore.product.coverImage"
            :src="checkoutStore.product.coverImage.url"
            :alt="
              checkoutStore.product.coverImage.alternativeText ||
              checkoutStore.product.title
            "
            class="h-16 w-16 rounded-lg object-cover shadow-lg"
          />
          <span
            v-if="discountPercent"
            class="absolute -right-2 -top-2 rounded-md bg-danger-fg px-1.5 py-0.5 text-xs font-bold text-onyx shadow"
          >
            -{{ discountPercent }}%
          </span>
        </div>
        <div>
          <h1 class="text-xl md:text-3xl font-bold text-mouse uppercase">
            {{ checkoutStore.product.title }}
          </h1>
          <p v-if="checkoutStore.product.subHeadline" class="text-rhino">
            {{ checkoutStore.product.subHeadline }}
          </p>
        </div>
      </div>

      <checkout-stepper
        :current-step="currentStep"
        @navigate="onStepNavigate"
      />
      <div class="mt-8">
        <checkout-step-configure v-if="currentStep === 1" />

        <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <!-- A floor on the step column, not on the page: the address step is
               far shorter than the others, and without one the footer rides up
               beside the sidebar. A full-viewport floor on the page leaves a
               screen of empty space below the shortest step instead. -->
          <div class="lg:col-span-8 lg:min-h-128">
            <checkout-step-auth v-if="currentStep === 2" />
            <checkout-step-profile v-else-if="currentStep === 3" />
            <checkout-step-create v-else-if="currentStep === 4" />
          </div>
          <div v-if="checkoutStore.price" class="hidden lg:block lg:col-span-4">
            <div class="sticky top-8">
              <checkout-price-breakdown :interactive="false" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import type { BreadCrumb } from '~/components/common/types';
import type { LatencyLocation } from '~/store/types';
import { REF_COOKIE_NAME, extractDatacenterSlug } from '~/helpers/checkout';
import { USER_CURRENCY_STORE_KEY } from '~/store/currency';

const { locale, t } = useI18n();

defineOptions({
  name: 'CheckoutWizardPage',
});

definePageMeta({
  layout: 'default',
});

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/checkout/[planId]/step/[stepNumber]',
    en: '/checkout/[planId]/step/[stepNumber]',
    pt: '/checkout/[planId]/step/[stepNumber]',
    fr: '/checkout/[planId]/step/[stepNumber]',
    nl: '/checkout/[planId]/step/[stepNumber]',
    it: '/checkout/[planId]/step/[stepNumber]',
    ja: '/checkout/[planId]/step/[stepNumber]',
    pl: '/checkout/[planId]/step/[stepNumber]',
    es: '/checkout/[planId]/step/[stepNumber]',
    zh: '/checkout/[planId]/step/[stepNumber]',
  },
});

const route = useRoute();
const router = useRouter();

const checkoutStore = useCheckoutStore();
const currencyStore = useCurrencyStore();
const authStore = useAuthStore();
const locationStore = useLocationStore();

useSeoMeta({
  title: () => {
    const step = Number(route.params.stepNumber);
    if (step !== 2 && checkoutStore.product?.name) {
      return t('checkout/page/title', { name: checkoutStore.product.name });
    }
    return t('checkout/page/order');
  },
});

const planId = computed(() => route.params.planId as string);
const stepNumber = computed(() => Number(route.params.stepNumber));

const initialized = ref(false);

// Capture ref query param before URL sync can strip it.
// Always update the cookie so a new ?ref= overrides a stale value.
const initialRef = route.query.ref
  ? String(
      Array.isArray(route.query.ref) ? route.query.ref[0] : route.query.ref,
    )
  : null;
const refCookie = useCookie(REF_COOKIE_NAME, { path: '/' });
if (initialRef) {
  refCookie.value = initialRef;
}

function refQueryParam(): Record<string, string> | undefined {
  return initialRef ? { ref: initialRef } : undefined;
}

function onStepNavigate(step: number): void {
  router.push({
    params: { ...route.params, stepNumber: String(step) },
    query: refQueryParam(),
  });
}

const storeProductKey = computed(() => checkoutStore.productKey);
const {
  couponCodes: autoCoupons,
  discountPercent,
  fetchDiscounts,
} = useCheckoutCoupons(planId, storeProductKey);

const currentStep = computed(() => {
  const step = stepNumber.value;
  if (step >= 1 && step <= 4) return step;
  return 1;
});

const breadcrumbs = computed<BreadCrumb[]>(() => {
  const crumbs: BreadCrumb[] = [];
  const product = checkoutStore.product;

  if (product?.slug) {
    // Only show "Game Servers" parent for gameserver products (not subscriptions/voiceserver)
    if (product.slug.startsWith('gameserver/')) {
      crumbs.push({
        title: t('general/menu/item/game_servers'),
        link: 'gameserver/index',
      });
    }
    crumbs.push({
      title: product.title,
      link: product.slug,
    });
  }

  crumbs.push({
    title: t('checkout/stepper/checkout'),
    link: '',
  });

  return crumbs;
});

// Validate step number and redirect if invalid
watch(
  stepNumber,
  (step) => {
    if (Number.isNaN(step) || step < 1 || step > 4) {
      router.replace({
        params: { ...route.params, stepNumber: '1' },
        query: refQueryParam(),
      });
    }
  },
  { immediate: true },
);

// Step guards — only enforce after store has been initialized
// (otherwise the guard fires before localStorage restore / initFromPlan)
watch(
  [currentStep, initialized],
  ([step, ready]) => {
    if (!ready) return;

    // Cannot access step 2+ without a complete configuration.
    // Step 3 is exempt — it handles its own state (region mismatch resolution,
    // profile verification) and may be active while reinitCheckout is in flight.
    if (step >= 2 && step !== 3 && !checkoutStore.complete) {
      router.replace({
        params: { ...route.params, stepNumber: '1' },
        query: refQueryParam(),
      });
      return;
    }

    // Cannot access step 3+ without authentication
    if (step >= 3 && !authStore.isAuthenticated) {
      router.replace({
        params: { ...route.params, stepNumber: '2' },
        query: refQueryParam(),
      });
      return;
    }

    // Cannot skip step 3 (region mismatch resolution) when there's a mismatch
    if (step >= 4 && checkoutStore.regionMismatch) {
      router.replace({
        params: { ...route.params, stepNumber: '3' },
        query: refQueryParam(),
      });
    }
  },
  { immediate: true },
);

// Re-fetch order information when the user switches currency via the footer
// (mirrors the pattern used on product landing pages).
async function reinitCheckout() {
  const newCurrency = currencyStore.currency || 'EUR';
  checkoutStore.fetchProduct(planId.value, locale.value, newCurrency);
  await checkoutStore.initFromPlan(planId.value, newCurrency);
}

useUserCurrency(reinitCheckout, reinitCheckout);

watch(
  () => authStore.activeRegion,
  () => reinitCheckout(),
);

// The step guard above only runs when the step changes, so a region switch
// made while sitting on step 4 would leave the buyer on a payment form for the
// wrong store. Send them back to step 3, which resolves the mismatch — the same
// place a reload of step 4 lands on.
watch(
  () => checkoutStore.regionMismatch,
  (mismatch) => {
    if (!mismatch || !initialized.value || currentStep.value < 4) return;
    router.replace({
      params: { ...route.params, stepNumber: '3' },
      query: refQueryParam(),
    });
  },
);

onMounted(async () => {
  // Read the user's stored currency preference directly so the first
  // initFromPlan call uses the correct currency. Without this,
  // useUserCurrency's onNuxtReady fires AFTER onMounted, detecting a
  // mismatch and triggering a redundant reinitCheckout cycle.
  const storedCurrency = import.meta.client
    ? localStorage.getItem(USER_CURRENCY_STORE_KEY)
    : null;
  const currency = storedCurrency || currencyStore.currency || 'EUR';

  // If the store still carries state from a different plan (e.g. user navigated
  // from plan X to plan Y without completing), reset everything first so stale
  // selections don't leak into API requests and cause a fetch loop.
  if (checkoutStore.planId && checkoutStore.planId !== planId.value) {
    checkoutStore.reset();
  }

  // Set planId on the checkout store
  checkoutStore.planId = planId.value;

  // Check for URL query params and initialize from them.
  // Config params (loc, dur, units, addons) come from bundle "ORDER NOW" links
  // and fully define the checkout selections. Meta-only params (pk, repo_key,
  // coupon) come from "custom order" or referral links and only identify the
  // product — selections should be reset so component watchers apply fresh
  // defaults (best-latency location, 30-day duration, default units).
  // Coupons are handled separately via auto-apply logic below.
  const query = route.query;
  const hasConfigParams = query.loc || query.dur || query.units || query.addons;
  const hasMetaOnly =
    !hasConfigParams && (query.pk || query.repo_key || query.coupon);

  let restored = false;
  if (hasConfigParams) {
    checkoutStore.initFromQueryParams(query);
  } else if (hasMetaOnly) {
    checkoutStore.resetConfigSelections();
    if (query.pk) checkoutStore.productKey = query.pk as string;
    if (query.repo_key) checkoutStore.repoKey = query.repo_key as string;
    if (query.coupon) {
      const raw = Array.isArray(query.coupon) ? query.coupon : [query.coupon];
      checkoutStore.couponCodes = raw
        .filter((c): c is string => Boolean(c))
        .flatMap((c) => c.split(','))
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
    }
  } else {
    restored = checkoutStore.restoreFromStorage();
  }

  // Use the restored currency when available so initFromPlan doesn't
  // detect a mismatch and wipe the selections we just restored.
  const effectiveCurrency = restored ? checkoutStore.currency : currency;

  // Fetch product data from Strapi (runs concurrently with initFromPlan,
  // awaited before fetchDiscounts so productKey is available for matching).
  const productPromise = checkoutStore
    .fetchProduct(planId.value, locale.value, effectiveCurrency)
    .catch(() => undefined);

  // Fetch order data first — critical for the page to display content.
  // Discounts/coupons are applied after so a hanging Apollo query can't
  // block the entire page from loading.
  // Force re-initialization when any query params are present — the store
  // may already have cached data for this plan from a previous visit, but
  // the user selected a different bundle or wants a fresh custom config.
  await checkoutStore.initFromPlan(
    planId.value,
    effectiveCurrency,
    !!(hasConfigParams || hasMetaOnly),
  );

  // Mark as initialized so step guards can now run
  initialized.value = true;

  // Run latency measurements for checkout locations. Use the datacenter slug
  // (extracted from the endpoint URL subdomain) so results are keyed
  // consistently with the global latency plugin's measurements.
  if (checkoutStore.locations.length > 0) {
    const latencyLocations: LatencyLocation[] = checkoutStore.locations
      .filter((loc) => loc.latencyEndpointUrl)
      .flatMap((loc) => {
        const slug = extractDatacenterSlug(loc.latencyEndpointUrl);
        if (!slug) return [];
        return [{ slug, endpoint: loc.latencyEndpointUrl }];
      });

    if (latencyLocations.length > 0) {
      locationStore.runLatencyMeasurements(latencyLocations);
    }
  }

  // Auto-apply coupon codes from reflink cookie + Strapi discounts.
  // Runs after order data is loaded so the page is already interactive.
  // Skip codes the user explicitly removed in a previous session.
  try {
    await productPromise;
    await fetchDiscounts();
    const normalizedCodes = autoCoupons.value
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    for (const code of normalizedCodes) {
      if (
        !checkoutStore.couponCodes.includes(code) &&
        !checkoutStore.dismissedAutoCodes.includes(code)
      ) {
        checkoutStore.couponCodes.push(code);
      }
    }
    // Store auto-applied codes so PriceBreakdown can show "Auto Applied" badges
    checkoutStore.autoAppliedCodes = normalizedCodes.filter(
      (code) =>
        checkoutStore.couponCodes.includes(code) &&
        !checkoutStore.dismissedAutoCodes.includes(code),
    );

    // Re-fetch price to include any newly auto-applied coupons.
    // Clear any error from fetchPrice so auto-apply stays non-blocking.
    if (checkoutStore.complete && checkoutStore.couponCodes.length > 0) {
      const priceErr = await checkoutStore.fetchPrice();
      if (priceErr) {
        checkoutStore.error = null;
      }
    }
  } catch (err) {
    console.debug('discount_auto_apply_failed', { planId: planId.value, err });
  }
});

// Sync checkout selections into the URL so the link is shareable.
// Watches both toQueryParams() and initialized so the URL is updated
// as soon as initialization completes (ensuring ?ref= is preserved).
watch(
  [() => checkoutStore.toQueryParams(), initialized],
  async ([params]) => {
    if (!initialized.value || currentStep.value !== 1) return;
    if (initialRef) params.ref = initialRef;
    try {
      const failure = await router.replace({ query: params });
      if (isNavigationFailure(failure, NavigationFailureType.duplicated))
        return;
    } catch {
      // URL sync is cosmetic — a guard rejection or transient router error
      // should not surface as an unhandled rejection / Sentry noise.
    }
  },
  { deep: true },
);
</script>
